import { useEffect, useState, useRef } from 'react';
import { X, Send } from 'lucide-react';
import api from '../api/client';
import useAuthStore from '../stores/authStore';
import { subscribe as subscribeAdminWs } from '../utils/wsAdmin';

/**
 * Ported from primus-admin-main's ChatPanel.jsx. Difference: history is
 * fetched pre-filtered to this PC server-side (GET /internal/chat/{cafeId}/
 * {pcId}, the whole two-way conversation) instead of fetching "my messages"
 * and filtering client-side -- a superadmin has no natural "my inbox" the
 * way a single café-admin does. Live delivery still rides the shared
 * /ws/admin chat.message broadcast, which already reaches every superadmin
 * connection regardless of café (see app/ws/admin.py's broadcast_admin).
 */
export default function ChatPanel({ pc, cafeId, onClose }) {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    // Read-only platform accounts see the thread but can't message the PC.
    const canSend = useAuthStore((s) => s.canActAsSuperAdmin());
    const messagesEndRef = useRef(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    useEffect(() => {
        if (!pc?.id || !cafeId) return;
        const pcId = Number(pc.id);

        const loadHistory = async () => {
            try {
                setLoading(true);
                const res = await api.get(`/internal/chat/${cafeId}/${pcId}`);
                setMessages(res.data || []);
            } catch (e) {
                console.error('Failed to load chat history', e);
            } finally {
                setLoading(false);
            }
        };

        loadHistory();

        const unsubscribe = subscribeAdminWs((msg) => {
            if (!msg || msg.event !== 'chat.message') return;
            const payload = msg.payload || {};
            const payloadPcId = payload.client_id != null ? Number(payload.client_id) : null;
            if (payloadPcId !== pcId) return;

            const incoming = {
                id: payload.message_id || payload.id,
                pc_id: payloadPcId,
                from_user_id: payload.from_user_id,
                to_user_id: payload.to_user_id,
                message: payload.text || payload.message,
                timestamp: new Date((payload.ts || Date.now() / 1000) * 1000).toISOString(),
                from: payload.from,
                user_name: payload.user_name,
            };
            setMessages((prev) => [...prev, incoming]);
        });

        return () => { unsubscribe && unsubscribe(); };
    }, [pc?.id, cafeId]);

    const sendMessage = async () => {
        const text = input.trim();
        if (!text || !pc || !cafeId) return;

        try {
            setSending(true);
            await api.post(`/internal/chat/${cafeId}`, { pc_id: pc.id, message: text });
            setInput('');
            // The sent message will arrive back via the chat.message WS event.
        } catch (e) {
            console.error('Failed to send chat message', e);
        } finally {
            setSending(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    if (!pc) return null;

    return (
        <div className="chatpanel-backdrop" onClick={onClose}>
            <div className="chatpanel" onClick={(e) => e.stopPropagation()}>
                <div className="chatpanel__header">
                    <div>
                        <div className="chatpanel__label">Chat with</div>
                        <div className="chatpanel__name">{pc.name}</div>
                    </div>
                    <button onClick={onClose} className="chatpanel__close"><X size={18} /></button>
                </div>
                <div className="chatpanel__messages">
                    {loading && <div className="chatpanel__hint">Loading conversation…</div>}
                    {!loading && messages.length === 0 && (
                        <div className="chatpanel__hint">No messages yet. Start the conversation.</div>
                    )}
                    {messages.map((m) => {
                        const isFromAdmin = m.from === 'admin';
                        return (
                            <div key={m.id || `${m.timestamp}-${m.from_user_id || ''}`} className={`chatpanel__row ${isFromAdmin ? 'chatpanel__row--admin' : 'chatpanel__row--client'}`}>
                                <div className="chatpanel__meta">
                                    <span className={isFromAdmin ? 'chatpanel__sender--admin' : 'chatpanel__sender--client'}>
                                        {isFromAdmin ? 'Admin' : (m.user_name || 'Client')}
                                    </span>
                                    <span>·</span>
                                    <span>{m.timestamp ? new Date(m.timestamp).toLocaleTimeString() : ''}</span>
                                </div>
                                <div className={`chatpanel__bubble ${isFromAdmin ? 'chatpanel__bubble--admin' : 'chatpanel__bubble--client'}`}>
                                    {m.message}
                                </div>
                            </div>
                        );
                    })}
                    <div ref={messagesEndRef} />
                </div>
                <div className="chatpanel__composer">
                    <textarea
                        rows={1}
                        className="chatpanel__input"
                        placeholder={canSend ? 'Type a message…' : 'Read-only access — you can read but not send'}
                        disabled={!canSend}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                    <button onClick={sendMessage} disabled={!canSend || sending || !input.trim()} className="btn btn--primary">
                        <Send size={16} /> Send
                    </button>
                </div>
            </div>

            <style>{`
                .chatpanel-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 10000; display: flex; align-items: center; justify-content: center; padding: 16px; }
                .chatpanel { width: 100%; max-width: 460px; max-height: 80vh; display: flex; flex-direction: column; background: var(--bg-elevated, #111827); border: 1px solid var(--glass-border, #374151); border-radius: var(--radius-lg, 12px); }
                .chatpanel__header { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; border-bottom: 1px solid var(--glass-border, #374151); }
                .chatpanel__label { font-size: var(--text-xs, 12px); color: var(--text-tertiary, #9CA3AF); }
                .chatpanel__name { font-weight: 600; font-size: var(--text-lg, 18px); color: #fff; }
                .chatpanel__close { color: var(--text-tertiary, #9CA3AF); background: none; border: none; cursor: pointer; }
                .chatpanel__messages { flex: 1; padding: 12px 16px; overflow-y: auto; display: flex; flex-direction: column; gap: 12px; min-height: 200px; max-height: 400px; }
                .chatpanel__hint { font-size: 12px; color: var(--text-tertiary, #6B7280); }
                .chatpanel__row { display: flex; flex-direction: column; }
                .chatpanel__row--admin { align-items: flex-end; }
                .chatpanel__row--client { align-items: flex-start; }
                .chatpanel__meta { font-size: 11px; color: var(--text-tertiary, #6B7280); display: flex; align-items: center; gap: 4px; margin-bottom: 2px; }
                .chatpanel__sender--admin { color: #818cf8; font-weight: 500; }
                .chatpanel__sender--client { color: #4ade80; font-weight: 500; }
                .chatpanel__bubble { display: inline-block; font-size: 14px; padding: 8px 12px; border-radius: 10px; max-width: 85%; white-space: pre-wrap; word-break: break-word; }
                .chatpanel__bubble--admin { background: rgba(99,102,241,0.8); color: #fff; border-bottom-right-radius: 2px; }
                .chatpanel__bubble--client { background: rgba(55,65,81,0.8); color: #f3f4f6; border-bottom-left-radius: 2px; }
                .chatpanel__composer { display: flex; gap: 8px; padding: 12px 16px; border-top: 1px solid var(--glass-border, #374151); }
                .chatpanel__input { flex: 1; background: rgba(255,255,255,0.06); border: 1px solid var(--glass-border, #374151); border-radius: 8px; padding: 8px 12px; font-size: 14px; color: #fff; resize: none; }
            `}</style>
        </div>
    );
}
